import {
  Button,
  FieldErrorText,
  FieldLabel,
  FieldRoot,
  Flex,
  Input,
} from '@chakra-ui/react';
import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';

import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { type CountryCode, isValidPhoneNumber } from 'libphonenumber-js';
import {
  NativeSelectField,
  NativeSelectRoot,
} from '~/components/ui/native-select';
import { toaster } from '~/components/ui/toaster';
import { useI18nLn } from '~/hooks/store';
import { fetchAddressByZipcode } from '~/lib/foreign_apis/kenall';
import { debounce } from '~/lib/utils/debounce';
import { translateCountryCodeToLocaleText } from '~/src/services/i18n/country';
import type { ShippingSpec } from '~/types/resources';
import type { DeliveryInfo } from '~/types/resources/deliveryInfo';

type Props = {
  onSubmit: (data: DeliveryInfo) => Promise<void>;
  shipableCountries: readonly ShippingSpec['country'][];
  isLoading: boolean;
};

export const DeliveryInfoForm: React.FC<Props> = (props: Props) => {
  const {
    register,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
    getValues,
  } = useFormContext<DeliveryInfo>();
  const { t, dictionary } = useI18nLn();
  const [, setAutoCompleteRequested] = useState<number>(Date.now());

  const handleAddressAutoFill = async () => {
    const handle = async () => {
      const formated_zip_code = getValues('zip_code');
      if (formated_zip_code == null || formated_zip_code == '') {
        setError('zip_code', {
          type: 'manual',
          message: '郵便番号を入力してください',
        });
        return;
      } else if (formated_zip_code.length !== 7) {
        setError('zip_code', {
          type: 'manual',
          message: t(
            'components.shipping_forms.errors.address_auto_fill_not_in_service'
          ),
        });
        return;
      }

      const address = await fetchAddressByZipcode(formated_zip_code);
      if (address.status === 200 && address.data.length > 0) {
        setValue('prefecture', address.data[0].prefecture);
        setValue('city', address.data[0].city + address.data[0].town);
        toaster.success({
          title: '住所を自動入力しました',
          duration: 2000,
        });
        clearErrors('zip_code');
      } else {
        toaster.error({
          title: '住所が見つかりませんでした',
          duration: 2000,
        });
        setError('zip_code', {
          type: 'manual',
          message: '住所が見つかりませんでした',
        });
      }
    };
    setAutoCompleteRequested((data) => {
      if (data + 1000 > Date.now()) {
        return data;
      }
      handle();
      return Date.now();
    });
  };

  return (
    <form onSubmit={handleSubmit(props.onSubmit)}>
      <FieldRoot
        pb="16px"
        readOnly={props.isLoading}
        invalid={errors.country != undefined}
      >
        <FieldLabel fontSize="13px" mb="4px">
          {t('components.shipping_forms.country')}
        </FieldLabel>
        <NativeSelectRoot w="50%" focusRingColor="primary">
          <NativeSelectField
            autoFocus={true}
            {...register('country', {
              required: t('components.shipping_forms.errors.is_required'),
              onChange: (e) => {
                setValue('country', e.target.value as DeliveryInfo['country']);
              },
            })}
          >
            {props.shipableCountries.map((country, i) => {
              return (
                <option key={i} value={country}>
                  {translateCountryCodeToLocaleText({
                    countryCode: country,
                    dictionary: dictionary,
                  })}
                </option>
              );
            })}
          </NativeSelectField>
        </NativeSelectRoot>
      </FieldRoot>

      <FieldRoot
        pb="16px"
        readOnly={props.isLoading}
        invalid={errors.fullname != undefined}
      >
        <FieldLabel fontSize="13px" mb="4px">
          {t('components.shipping_forms.name')}
        </FieldLabel>
        <Input
          size="md"
          variant="flushed"
          type="text"
          autoComplete="name"
          placeholder="ROLL 太郎"
          {...register('fullname', {
            required: t('components.shipping_forms.errors.is_required'),
          })}
        />
        <FieldErrorText>{errors.fullname?.message}</FieldErrorText>
      </FieldRoot>

      <FieldRoot
        pb="16px"
        readOnly={props.isLoading}
        invalid={errors.phone_number != undefined}
      >
        <FieldLabel fontSize="13px" mb="4px">
          {t('components.shipping_forms.phone')}
        </FieldLabel>
        <PhoneInput
          country={
            getValues('phone_number_country_code')?.toLocaleLowerCase() ?? 'jp'
          } // 国の初期値として選択された配送先国を設定
          value={getValues('phone_number')}
          autoFormat={false}
          enableSearch={true}
          enableAreaCodes={true}
          disableCountryCode={true}
          disableSearchIcon={true}
          preferredCountries={props.shipableCountries.map(
            (country) => country.toLocaleLowerCase() // 配送可能にしている国をリストの上位に優先する
          )}
          isValid={errors.phone_number != undefined}
          placeholder="000-0000-0000"
          inputProps={{
            type: 'tel',
            autoComplete: 'tel',
          }}
          inputStyle={{
            backgroundColor: 'white',
            borderWidth: '0px',
            borderBottomWidth: '1px',
            borderColor: errors.phone_number ? 'red' : 'gray',
            // TODO: focusが渡せないのでコンポーネントを自作するなどする
          }}
          buttonStyle={{
            borderWidth: '0px',
          }}
          {...(register('phone_number'),
          {
            onChange: (value: string, country: { countryCode?: string }) => {
              const countryCode = country.countryCode?.toUpperCase() as
                | CountryCode
                | undefined;

              if (!countryCode || !value) {
                setError('phone_number', {
                  type: 'manual',
                  message: t(
                    'components.shipping_forms.errors.is_invalid_phone_number'
                  ),
                });
                return;
              }

              if (
                isValidPhoneNumber(
                  value,
                  countryCode.toLocaleUpperCase() as CountryCode
                )
              ) {
                clearErrors('phone_number');
                setValue('phone_number', value);
                setValue(
                  'phone_number_country_code',
                  countryCode.toLocaleLowerCase()
                );
              } else {
                setError('phone_number', {
                  type: 'manual',
                  message: t(
                    'components.shipping_forms.errors.is_invalid_phone_number'
                  ),
                });
              }
            },
          })}
        />
        <FieldErrorText>{errors.phone_number?.message}</FieldErrorText>
      </FieldRoot>

      <FieldRoot
        pb="16px"
        readOnly={props.isLoading}
        invalid={errors.email != undefined}
      >
        <FieldLabel fontSize="13px" mb="4px">
          {t('components.shipping_forms.email')}
        </FieldLabel>
        <Input
          size="md"
          variant="flushed"
          type="email"
          autoComplete="email"
          placeholder="ROLL@tokyo.com"
          css={{
            focusBorderColor: 'primary',
          }}
          {...register('email', {
            required: t('components.shipping_forms.errors.is_required'),
            pattern: {
              value:
                /^[a-zA-Z0-9_+-]+(.[a-zA-Z0-9_+-]+)*@([a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*\.)+[a-zA-Z]{2,}$/,
              message: t('components.shipping_forms.errors.is_invalid_email'),
            },
          })}
        />
        <FieldErrorText>{errors.email?.message}</FieldErrorText>
      </FieldRoot>

      <FieldRoot
        pb="16px"
        readOnly={props.isLoading}
        invalid={errors.zip_code != undefined}
      >
        <FieldLabel fontSize="13px" mb="4px">
          {t('components.shipping_forms.zip_code')}
        </FieldLabel>

        <Flex>
          <Input
            w="100px"
            size="md"
            variant="flushed"
            type="number"
            css={{
              focusBorderColor: 'primary',
            }}
            autoComplete="postal-code"
            placeholder="0000000"
            {...register('zip_code', {
              onBlur: debounce(handleAddressAutoFill, 500),
              required: t('components.shipping_forms.errors.is_required'),
              validate: (value: string) => {
                const pattern = RegExp(/^[0-9]{3,9}$/);
                const check = pattern.test(value);
                return (
                  check ||
                  t('components.shipping_forms.errors.is_zip_code_out_of_range')
                );
              },
            })}
          />

          <Button
            w="104px"
            h="32px"
            size="md"
            variant="outline"
            rounded="50px"
            borderColor="border"
            fontSize="14px"
            color="main"
            onClick={handleAddressAutoFill}
          >
            {t('components.shipping_forms.auto_fill')}
          </Button>
        </Flex>
        <FieldErrorText>{errors.zip_code?.message}</FieldErrorText>
      </FieldRoot>

      <FieldRoot
        pb="16px"
        readOnly={props.isLoading}
        invalid={errors.prefecture != undefined}
      >
        <FieldLabel fontSize="13px" mb="4px">
          {t('components.shipping_forms.prefecture')}
        </FieldLabel>
        <Input
          size="md"
          variant="flushed"
          css={{
            focusBorderColor: 'primary',
          }}
          type="text"
          autoComplete="address-level1"
          placeholder="ROLL 県"
          {...register('prefecture', {
            required: t('components.shipping_forms.errors.is_required'),
          })}
        />
        <FieldErrorText>{errors.prefecture?.message}</FieldErrorText>
      </FieldRoot>

      <FieldRoot
        pb="16px"
        readOnly={props.isLoading}
        invalid={errors.city != undefined}
      >
        <FieldLabel fontSize="13px" mb="4px">
          {t('components.shipping_forms.city')}
        </FieldLabel>
        <Input
          size="md"
          variant="flushed"
          css={{
            focusBorderColor: 'primary',
          }}
          type="text"
          autoComplete="address-level2"
          placeholder="ROLL 市"
          {...register('city', {
            required: t('components.shipping_forms.errors.is_required'),
          })}
        />
        <FieldErrorText>{errors.city?.message}</FieldErrorText>
      </FieldRoot>

      <FieldRoot
        pb="16px"
        readOnly={props.isLoading}
        invalid={errors.address1 != undefined}
      >
        <FieldLabel fontSize="13px" mb="4px">
          {t('components.shipping_forms.address1')}
        </FieldLabel>
        <Input
          size="md"
          variant="flushed"
          css={{
            focusBorderColor: 'primary',
          }}
          type="text"
          autoComplete="address-level3"
          placeholder="ROLL 丁目 ROLL 番地"
          {...register('address1', {
            required: t('components.shipping_forms.errors.is_required'),
          })}
        />
        <FieldErrorText>{errors.address1?.message}</FieldErrorText>
      </FieldRoot>

      <FieldRoot
        readOnly={props.isLoading}
        invalid={errors.address2 != undefined}
      >
        <FieldLabel fontSize="13px" mb="4px">
          {t('components.shipping_forms.address2')}
        </FieldLabel>
        <Input
          size="md"
          variant="flushed"
          css={{
            focusBorderColor: 'primary',
          }}
          type="text"
          autoComplete="address-level4"
          placeholder="ROLL 000号室"
          {...register('address2')}
        />
        <FieldErrorText>{errors.address2?.message}</FieldErrorText>
      </FieldRoot>
    </form>
  );
};
